import * as React from 'react';
import { useEffect, useRef } from 'react';
import Autosuggest from 'react-autosuggest';

import { omit } from 'lodash';
import qs from 'qs';

import type { useRetracked } from 'js/lib/retracked';
import { useLocation, useNavigate } from 'js/lib/useRouter';

import { useTracker } from '@coursera/event-pulse/react';

import AutoCompleteSuggestion from 'bundles/search-common/components/searchbar/AutoCompleteSuggestion';
import {
  MAX_SCREEN_WIDTH_FOR_MOBILE_SEARCH_BAR,
  ON_SITE_SEARCH_PATH,
  SEARCH_AUTOCOMPLETE_MAIN_INDEX_ID,
  SEARCH_BAR_LOCATION,
} from 'bundles/search-common/constants';
import type { SearchBarLocation } from 'bundles/search-common/constants';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import type { AutocompleteSection, Hits, SuggestionHits } from 'bundles/search-common/types/autocomplete';
import { getDataToTrackFromHit } from 'bundles/search-common/utils/SearchUtils';
import { getAutocompleteLegacyEventingData } from 'bundles/search-common/utils/autocomplete';
import { isInFilterTestVariant } from 'bundles/search-common/utils/experimentUtils';
import { extractFiltersForEventing } from 'bundles/search-common/utils/providerEventingUtils';

import _t from 'i18n!nls/search-common';

export type Props = {
  autocompleteSuggestions: AutocompleteSection[];
  isSearchPage?: boolean;
  pageLocation?: SearchBarLocation;
  searchIsOpen?: boolean;
  isFocused?: boolean;
  onSearchFocus?: () => void;
  shouldFocusSearch?: boolean;
  shouldShowExposedSearchAndReg?: boolean;
  track: ReturnType<typeof useRetracked>;
  storeInputReference: (autosuggest: { input: HTMLInputElement | undefined } | null) => void;
  onSuggestionHighlighted: (param: { suggestion: SuggestionHits }) => void;
  blurAutocomplete: () => void;
  currentText: string;
  setCurrentText: (text: string) => void;
  autocompleteSuggestionsAvailable: boolean;
  setIsFocused: (focus: boolean) => void;
  debouncedAriaTextUpdate: () => void;
  debouncedRefineAndTrack: (value: string) => void;
};

type ItemsContainer = { containerProps: Record<string, unknown>; children: React.ReactNode };
const renderItemsContainer = ({ containerProps, children }: ItemsContainer) => {
  const role = children ? `${containerProps.role}` : undefined;
  return (
    <div {...containerProps} role={role} aria-label={_t('Autocomplete results')}>
      {children}
    </div>
  );
};
const renderSectionTitle = (section: { sectionTitle: string; hits: Hits[] }) => {
  const { sectionTitle } = section;
  if (!section || section.hits.length === 0 || !sectionTitle) return null;

  return (
    <div className="section-title-box horizontal-box">
      <span className="headline-1-text section-title">{sectionTitle}</span>
    </div>
  );
};

export const AutoCompleteInput = (props: Props) => {
  const {
    autocompleteSuggestions,
    shouldFocusSearch,
    pageLocation,
    track,
    isSearchPage,
    shouldShowExposedSearchAndReg,
    onSearchFocus,
    storeInputReference,
    onSuggestionHighlighted,
    blurAutocomplete,
    currentText,
    setCurrentText,
    autocompleteSuggestionsAvailable,
    isFocused,
    setIsFocused,
    debouncedAriaTextUpdate,
    debouncedRefineAndTrack,
    searchIsOpen,
  } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const tracker = useTracker();
  const { getSearchResults } = useSearch();
  const indexName = (getSearchResults(SEARCH_AUTOCOMPLETE_MAIN_INDEX_ID) || {})?.source?.indexName;
  const suggestionSelected = useRef(false);
  const page = window?.location.pathname;
  const shouldForceOpenForMobile =
    isSearchPage && isFocused && window?.innerWidth <= MAX_SCREEN_WIDTH_FOR_MOBILE_SEARCH_BAR;

  useEffect(() => {
    const removeAttr = () => {
      const nodeToEdit = document.querySelector('.react-autosuggest__container');
      nodeToEdit?.removeAttribute('aria-expanded');
    };
    removeAttr();
  });

  const placeholder =
    pageLocation === SEARCH_BAR_LOCATION.FrontPage || pageLocation === SEARCH_BAR_LOCATION.FrontPageBanner
      ? _t(`Search 'python' or 'front-end developer'`)
      : _t('What do you want to learn?');

  const onKeyDown = async (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      // This is done so that the container is closed when pressing escape
      blurAutocomplete();
    }
    // Without the second check this logic would also happen if the user selected a suggestion via the keyboard, so we set this.suggestionSelected
    // to true on onSuggestionSelected
    else if (event.key === 'Enter' && !suggestionSelected.current) {
      const otherQueryParams = omit(location.query, ['query', 'page', 'sortBy']);
      const otherQueryParamsString = qs.stringify(otherQueryParams);
      const newSearchProviderUrl = `${ON_SITE_SEARCH_PATH}?query=${encodeURIComponent(currentText)}`;

      const whitespacelessSearchText = currentText.replace(/\s+/g, ' '); // white space collapsed
      const partialUrl = `${ON_SITE_SEARCH_PATH}?query=${encodeURIComponent(whitespacelessSearchText)}`;
      const urlWithQuery = otherQueryParamsString ? `${partialUrl}&${otherQueryParamsString}` : partialUrl;

      tracker('perform_search', {
        searchedQuery: currentText,
        filter: extractFiltersForEventing(urlWithQuery),
      });

      track({
        action: 'search',
        trackingName: 'autocomplete_query',
        trackingData: { searchText: currentText, page, autocompleteSuggestionsAvailable, indexName },
      });
      if (isSearchPage) {
        // We add query to the url because we are using separate Algolia InstantSearch components for search results
        // & autocomplete, and using url parameters to sync state between the AutoComplete component & search page
        navigate(isInFilterTestVariant() ? urlWithQuery : newSearchProviderUrl);
        blurAutocomplete();
      } else {
        // If not on the search page, we want to navigate to search page with full refresh
        track({
          action: 'search',
          trackingName: 'algolia_search_page_query_updated',
          trackingData: { searchText: currentText, page, indexName },
        });
        window?.location.assign(urlWithQuery);
      }
    }
    // We now set it back to false to indicate that the selection has been finalized & if the user presses a key again it won't be
    // interpreted as selected from the menu unless they select from the menu, which will cause onSuggestionSelected is fired again and set it
    // back to true
    suggestionSelected.current = false;
  };

  const onFocus = () => {
    debouncedAriaTextUpdate();
    track({
      action: 'search',
      trackingName: 'autocomplete_focused',
      trackingData: { page: window?.location.pathname, indexName },
    });
    setIsFocused(true);
    if (shouldShowExposedSearchAndReg) {
      onSearchFocus?.();
    }
  };

  const onChange = (_event: React.SyntheticEvent, { newValue, method }: { newValue: string; method: string }) => {
    // When there's an input change on AutoComplete, we update with new value
    // if the user typed or clicked, we call refine to update the AutoComplete suggestions
    setCurrentText(newValue || '');
    debouncedAriaTextUpdate();
    if (['type', 'click'].includes(method) && newValue !== currentText) {
      debouncedRefineAndTrack(newValue);
    }
  };

  const onSuggestionSelected = async (
    _event: React.FormEvent,
    { suggestion, method }: { suggestion: Hits; method: string }
  ) => {
    // since alwaysRenderSuggestions is set to true under certain conditions like IsFocused,
    // there's need to always set the focus to false when a suggestion is selection
    setIsFocused(false);
    // If selected using enter key, we set this to true to avoid operation on onKeyDown, where we will set it back to false
    if (method === 'enter') suggestionSelected.current = true;

    const otherQueryParams = omit(location.query, 'query');

    const otherQueryParamsString = qs.stringify({
      ...otherQueryParams,
    });

    const results = getAutocompleteLegacyEventingData(suggestion, currentText);
    if (suggestion.objectID === 'onboarding_link') {
      tracker('click_button', {
        button: {
          name: 'start_onboarding',
        },
        form: {
          name: 'search_dropdown',
        },
      });
    }
    tracker('click_autocomplete_suggestions', results);
    // The logic here: go to the url if available. If we are not in the search page, we'll
    // navigate to the search page when the user selects a suggested search.
    const trackingData = getDataToTrackFromHit(suggestion);
    if (suggestion.objectUrl) {
      track({
        action: 'search',
        trackingName: suggestion.trackingName,
        trackingData: { ...trackingData, searchText: currentText, page, hitType: suggestion.trackingName },
      });
      window?.location.assign(`${suggestion.objectUrl}?${otherQueryParamsString}`);
    } else {
      const partialUrl = `${ON_SITE_SEARCH_PATH}?query=${encodeURIComponent(suggestion.name)}`;
      const urlWithQuery = otherQueryParamsString ? `${partialUrl}&${otherQueryParamsString}` : partialUrl;
      const newSearchProviderUrl = `${ON_SITE_SEARCH_PATH}?query=${encodeURIComponent(suggestion.name)}`;

      tracker('perform_search', {
        searchedQuery: currentText,
        sourceLinkCategory: 'autocomplete_suggestions',
        filter: extractFiltersForEventing(urlWithQuery),
      });

      track({
        action: 'search',
        trackingName: suggestion.trackingName,
        trackingData: { ...trackingData, searchText: currentText, page, hitType: suggestion.trackingName },
      });
      if (isSearchPage) {
        navigate(newSearchProviderUrl);
      } else {
        window?.location.assign(urlWithQuery);
      }
    }
  };
  return (
    <Autosuggest
      suggestions={autocompleteSuggestions}
      multiSection={true}
      focusInputOnSuggestionClick={false}
      // we are using onChange rather than the following 2 props since it's a more common pattern to handle input changes
      onSuggestionsFetchRequested={() => ({})}
      onSuggestionSelected={onSuggestionSelected}
      onSuggestionsClearRequested={() => ({})}
      onSuggestionHighlighted={onSuggestionHighlighted}
      getSuggestionValue={(hit) => hit && hit.name}
      renderSuggestion={AutoCompleteSuggestion}
      renderSuggestionsContainer={renderItemsContainer}
      shouldRenderSuggestions={() => true}
      alwaysRenderSuggestions={searchIsOpen || shouldForceOpenForMobile}
      inputProps={{
        id: 'search-autocomplete-input',
        placeholder,
        value: currentText,
        onChange,
        onKeyDown,
        onFocus,
        autoFocus: shouldFocusSearch,
        'aria-label': placeholder,
        role: 'textbox',
        'aria-controls': 'autocompleteARIASuggestions',
      }}
      renderSectionTitle={renderSectionTitle}
      getSectionSuggestions={(section) => section?.hits ?? []}
      ref={storeInputReference}
    />
  );
};

export default React.memo(AutoCompleteInput);
